<script setup lang="ts">
import { storeToRefs } from 'pinia'

import VCard from '~/components/ui/VCard.vue'

import { useComputeMismatches } from '~/composables/useComputeMismatches'
import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useUserStore } from '~/stores/user'

import { provideFetchRecentOpportunities } from '~/api-core/Curators/FetchRecentOpportunities'

import type {
  Influencer,
  InfluencerPreferences,
  InfluencerRecentOpportunities,
} from '~/types/influencer'

type Props = {
  genresConfiguration: InfluencerPreferences
  influencer: Influencer
}

type Slots = {
  'post-content'(): any
}

const props = defineProps<Props>()
defineSlots<Slots>()

const { coreFetch } = useProvideCoreFetch()
const { getInfluencerExpectationsData } = useComputeMismatches()
const { isContest } = useCuratorContestsInfos(props.influencer)

const { IS_LOGGED_IN, IS_INF } = storeToRefs(useUserStore())

const { data: recentOpportunities } = useAsyncData<
  InfluencerRecentOpportunities[]
>(
  `recentOpportunities-${props.influencer.slug}`,
  () => {
    const fetchRecentOpportunities = provideFetchRecentOpportunities(
      coreFetch,
      props.influencer.slug,
    )
    return fetchRecentOpportunities()
  },
  {
    default: () => [],
    dedupe: 'defer',
  },
)

const influencerExpectationsData = getInfluencerExpectationsData(
  props.influencer,
)

const showRecentOpportunities = computed<boolean>(
  () => recentOpportunities.value.length > 0,
)
const genresAcceptedMostOftenIds = computed<number[]>(
  () => props.influencer.latest_stats?.genres_accepted_often ?? [],
)
</script>

<template>
  <div class="tw-flex tw-w-full tw-flex-col tw-gap-6">
    <VCard class="tw-w-full" padding="5">
      <LazyIdleInfluencerProfileAboutDescription
        :influencer="influencer"
        hydrate-on-idle
      />
      <slot name="post-content" />
    </VCard>
    <LazyNeverInfluencerProfileAboutAcceptedGenres
      v-if="genresAcceptedMostOftenIds"
      :genre-ids="genresAcceptedMostOftenIds"
      hydrate-never
    />
    <LazyNeverInfluencerProfileAboutGenresList
      v-for="([target, tagInformations], index) in Object.entries(
        genresConfiguration,
      )"
      :key="index"
      :target="target"
      :tag-informations="tagInformations"
      hydrate-never
    />
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <LazyNeverCardsHelpBlock
        v-if="!IS_INF"
        :link="$t('influencer.profile.genresFAQ.link')"
        :title="$t('influencer.profile.genresFAQ.title')"
        :description="$t('influencer.profile.genresFAQ.subtitle')"
        hydrate-never
      />
    </ClientOnly>
    <LazyNeverInfluencerProfileAboutFavorite
      :influencer="influencer"
      hydrate-never
    />
    <LazyNeverVRecentOpportunities
      v-if="showRecentOpportunities"
      :recent-opportunities="recentOpportunities"
      :influencer="influencer"
      hydrate-never
    />
    <LazyNeverInfluencerProfileAboutConditions
      v-if="isContest"
      :influencer="influencer"
      hydrate-never
    />
    <LazyNeverInfluencerProfileAboutPlan
      v-if="isContest"
      :influencer="influencer"
      hydrate-never
    />
    <LazyNeverInfluencerProfileAboutExpectations
      :expectations-data="influencerExpectationsData"
      hydrate-never
    />
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <LazyNeverInfluencerProfileAboutSeoLists
        v-if="!IS_LOGGED_IN"
        class="tw-w-full"
        :seo-pages="influencer.seo_pages"
        hydrate-never
      />
    </ClientOnly>
  </div>
</template>
